import {
    ApiClient,
    ApiClientError,
    useAxiosClient,
} from '@amzn/event-engine-js-utils';
import { AuthContext, IdentityProvider } from 'contexts/AuthProvider';
import useContentBuild from 'hooks/use-content-build';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

export interface PublishedConfig {
    current: string;
    currentV2: {
        buildId: string;
        location: string;
        requiresAuth: boolean;
    };
    previous: string[];
    previousV2: string[];
}

interface UseEmbeddedBuildOutput {
    /**
     * Boolean indicating async workflows are loading
     */
    loading: boolean;
    /**
     * Errors from API calls to published.json or AppSettings
     */
    error: boolean | ApiClientError;
    /**
     * The baseUrl that the PreviewComponent will use to make requests
     */
    publishedContentUrl: string;
    /**
     * Kinesis stream ID that tracks locale of workshop
     */
    kinesisFirehoseStreamLocale?: string;
    /**
     * BaseURL for the published.json file
     */
    publishedConfigBaseUrl?: string;
    /**
     * The build id of the content that is currently published
     */
    contentBuildId?: string;
    /**
     * Determines whether or not the piece of content is gated
     * to internal users and requires auth
     */
    requiresAuth?: boolean;
    /**
     * The content id for the piece of content
     */
    contentId?: string;
}

const QUICK_LINK_PATH = 'quicklinks';
export const CONFIG_FILE = 'published.json';

const useEmbeddedBuild = (): UseEmbeddedBuildOutput => {
    const { signIn, getCurrentSession } = useContext(AuthContext);
    const [authCheckComplete, setAuthCheckComplete] = useState<boolean>(false);
    const {
        isLoading,
        contentId,
        publicContentBaseUrl,
        isError,
        kinesisFirehoseStreamLocale,
        quickLinkId,
    } = useContentBuild();
    const publishedConfigClient = useRef(
        ApiClient.createGenericClient({
            clientName: 'PublishedConfigClient',
            baseUrl: '',
        })
    );
    const {
        get: {
            request: publishedConfigRequest,
            response: {
                isError: publishedConfigError,
                isLoading: publishedConfigLoading,
                data: publishedConfigData,
            },
        },
    } = useAxiosClient<PublishedConfig>(publishedConfigClient.current);

    /**
     * Retrieve published.json config file. This contains the
     * current content build ID to render.
     *
     * We currently support either the contentID in the URL, or a quicklink.
     */
    useEffect(() => {
        if (!publicContentBaseUrl || !(contentId || quickLinkId)) {
            return;
        }

        if (contentId) {
            publishedConfigClient.current.setBaseUrl(
                `${publicContentBaseUrl}/${contentId}`
            );
        } else {
            publishedConfigClient.current.setBaseUrl(
                `${publicContentBaseUrl}/${QUICK_LINK_PATH}/${quickLinkId}`
            );
        }

        publishedConfigRequest(CONFIG_FILE);
    }, [publicContentBaseUrl, contentId]);

    useEffect(() => {
        publishedConfigData &&
            (async () => {
                /**
                 * Keep backwards compatibility if currentV2 does not exist.
                 * If the workshop does not require auth, mark them as
                 * authenticated.
                 */
                if (
                    !publishedConfigData?.data?.currentV2 ||
                    !publishedConfigData?.data?.currentV2?.requiresAuth
                ) {
                    setAuthCheckComplete(true);
                    return;
                }

                const currentSession = await getCurrentSession(
                    IdentityProvider.AMAZON_FEDERATE
                );

                if (!currentSession) {
                    signIn(IdentityProvider.AMAZON_FEDERATE);
                    return;
                }

                setAuthCheckComplete(true);
            })();
    }, [publishedConfigData]);

    /**
     * Construct the content base URL with the current
     * published content build ID
     */
    const publishedContentUrl = useMemo(() => {
        if (!publishedConfigData?.data || !authCheckComplete) {
            return '';
        }

        // backwards compatibility if V2 does not exist
        const buildId =
            publishedConfigData.data.currentV2?.buildId ||
            publishedConfigData.data.current;

        return `${publicContentBaseUrl}/${buildId}/`;
    }, [authCheckComplete, publishedConfigData]);

    const loading = publishedConfigLoading || isLoading || !authCheckComplete;
    const error = publishedConfigError || isError;

    return {
        contentId,
        loading,
        error,
        publishedContentUrl,
        kinesisFirehoseStreamLocale,
        contentBuildId:
            publishedConfigData?.data?.currentV2?.buildId ||
            publishedConfigData?.data?.current,
        requiresAuth: publishedConfigData?.data?.currentV2?.requiresAuth,
        publishedConfigBaseUrl: publishedConfigClient.current.defaults.baseURL,
    };
};

export default useEmbeddedBuild;
